<div class="appointments-list-container">
  <!-- Header -->
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Appointments List</h1>
        <p>View and manage all appointments</p>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <div class="filters-section">
    <div class="filter-header">
      <h3><i class="fas fa-filter"></i> Filter Appointments</h3>
      <button 
        class="clear-filters-btn" 
        (click)="clearFilters()"
        [disabled]="!hasActiveFilters() || loading">
        <i class="fas fa-times"></i> Clear Filters
      </button>
    </div>
    
    <div class="filters-grid">
      <div class="filter-group">
        <label for="dateFrom">From Date:</label>
        <input 
          type="date" 
          id="dateFrom"
          [(ngModel)]="filters.dateFrom" 
          (change)="onFilterChange()"
          class="filter-input">
      </div>

      <div class="filter-group">
        <label for="dateTo">To Date:</label>
        <input 
          type="date" 
          id="dateTo"
          [(ngModel)]="filters.dateTo" 
          (change)="onFilterChange()"
          class="filter-input">
      </div>

      <div class="filter-group">
        <label for="statusFilter">Status:</label>
        <div class="status-dropdown-container">
          <button
            type="button"
            id="statusFilter"
            (click)="toggleStatusDropdown()"
            class="status-dropdown-button"
            [class.active]="statusDropdownOpen || selectedStatuses.length > 0">
            <span>{{ getStatusDisplayText() }}</span>
            <i class="fas" [class.fa-chevron-down]="!statusDropdownOpen" [class.fa-chevron-up]="statusDropdownOpen"></i>
          </button>
          <div class="status-dropdown-menu" *ngIf="statusDropdownOpen">
            <div class="status-dropdown-header">
              <span>Select Status(es)</span>
              <button 
                type="button"
                (click)="selectedStatuses = []; onFilterChange(); $event.stopPropagation()"
                class="clear-status-btn"
                *ngIf="selectedStatuses.length > 0">
                Clear All
              </button>
            </div>
            <div class="status-options">
              <label 
                *ngFor="let option of statusOptions" 
                class="status-option-item"
                (click)="$event.stopPropagation()">
                <input
                  type="checkbox"
                  [checked]="isStatusSelected(option.value)"
                  (change)="toggleStatus(option.value)"
                  class="status-checkbox">
                <span class="status-label">{{ option.label }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="filter-group">
        <label for="zoneFilter">Zone:</label>
        <app-district-zone-filter
          [(ngModel)]="selectedGeoZones"
          (ngModelChange)="onFilterChange()">
        </app-district-zone-filter>
      </div>

      <div class="filter-group">
        <label for="agentFilter">Agent:</label>
        <div class="status-dropdown-container">
          <button
            type="button"
            id="agentFilter"
            (click)="toggleAgentDropdown()"
            class="status-dropdown-button"
            [class.active]="agentDropdownOpen || selectedAgents.length > 0">
            <span>{{ getAgentDisplayText() }}</span>
            <i class="fas" [class.fa-chevron-down]="!agentDropdownOpen" [class.fa-chevron-up]="agentDropdownOpen"></i>
          </button>
          <div class="status-dropdown-menu" *ngIf="agentDropdownOpen">
            <div class="status-dropdown-header">
              <span>Select Agent(s)</span>
              <button 
                type="button"
                (click)="selectedAgents = []; onFilterChange(); $event.stopPropagation()"
                class="clear-status-btn"
                *ngIf="selectedAgents.length > 0">
                Clear All
              </button>
            </div>
            <div class="status-options">
              <label 
                *ngFor="let agent of allAgents" 
                class="status-option-item"
                (click)="$event.stopPropagation()">
                <input
                  type="checkbox"
                  [checked]="isAgentSelected(agent.agentId)"
                  (change)="toggleAgent(agent.agentId)"
                  class="status-checkbox">
                <span class="status-label">{{ agent.name }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="filter-group">
        <label for="customerNameFilter">Customer Name:</label>
        <input 
          type="text" 
          id="customerNameFilter"
          [(ngModel)]="filters.customerName" 
          placeholder="Search by customer name..."
          (input)="onFilterChange()"
          class="filter-input">
      </div>

      <div class="filter-group">
        <label for="phoneFilter">Phone Number:</label>
        <input 
          type="text" 
          id="phoneFilter"
          [(ngModel)]="filters.phoneNumber" 
          placeholder="Search by phone number..."
          (input)="onFilterChange()"
          class="filter-input">
      </div>

      <div class="filter-actions">
        <button 
          class="apply-filters-btn" 
          (click)="applyFilters()"
          [disabled]="loading">
          <i class="fas fa-search"></i> Apply Filters
        </button>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage" class="error-message">
    <i class="fas fa-exclamation-circle mr-2"></i>
    {{ errorMessage }}
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <div class="loading-spinner"></div>
    <span class="loading-text">Loading appointments...</span>
  </div>

  <!-- Results Summary and View Toggle -->
  <div *ngIf="!loading && appointments.length > 0" class="results-summary-container">
    <div class="results-summary">
      <p>Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} appointments</p>
    </div>
    <div class="view-toggle-container">
      <button 
        class="view-toggle-btn"
        [class.active]="viewMode === 'grid'"
        (click)="switchToGridView()"
        title="Grid View">
        <i class="fas fa-table"></i> Grid
      </button>
      <button 
        class="view-toggle-btn"
        [class.active]="viewMode === 'map'"
        (click)="switchToMapView()"
        title="Map View">
        <i class="fas fa-map"></i> Map
      </button>
    </div>
  </div>

  <!-- Desktop Table View -->
  <div *ngIf="!loading && appointments.length > 0 && viewMode === 'grid'" class="desktop-table-container">
    <div class="table-wrapper">
      <table class="appointments-table">
        <thead>
          <tr>
            <th>Customer Name</th>
            <th>Date</th>
            <th>Agent Name</th>
            <th>Zone</th>
            <th>Status</th>
            <th>Time Slot</th>
            <th>Remarks</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let appointment of appointments" class="appointment-row">
            <td class="customer-name">
              {{ appointment.customerName || 'N/A' }}
              <span *ngIf="appointment.parentId" class="rescheduled-indicator" title="Created through rescheduling">
                <i class="fas fa-redo-alt"></i> Rescheduled
              </span>
            </td>
            <td class="date">{{ formatDate(appointment.date) }}</td>
            <td class="agent-name">{{ appointment.agentName || 'N/A' }}</td>
            <td class="zone">{{ appointment.geoZoneName || 'N/A' }}</td>
            <td>
              <span class="status-badge" [ngClass]="getStatusClass(appointment.status)">
                {{ appointment.status || 'N/A' }}
              </span>
            </td>
            <td class="time-slot">{{ appointment.timeSlot || 'N/A' }}</td>
            <td class="remarks">
              {{ (appointment.remarkHistory && appointment.remarkHistory.length > 0) ? appointment.remarkHistory[appointment.remarkHistory.length - 1].text : (appointment.remarks || 'N/A') }}
            </td>
            <td class="actions-cell">
              <button 
                class="view-appointment-btn"
                (click)="navigateToDetail(appointment)"
                title="View Appointment">
                <i class="fas fa-eye"></i> View
              </button>
              <button 
                *ngIf="canUpdateFees(appointment)"
                class="update-fees-btn"
                (click)="onUpdateFees(appointment)"
                title="Update Appointment Fees">
                <i class="fas fa-dollar-sign"></i> Update Fees
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Mobile Card View -->
  <div *ngIf="!loading && appointments.length > 0 && viewMode === 'grid'" class="mobile-cards-container">
    <div *ngFor="let appointment of appointments" class="appointment-card">
      <div class="card-header">
        <div class="card-header-left">
          <h3>
            {{ appointment.customerName || 'N/A' }}
            <span *ngIf="appointment.parentId" class="rescheduled-tag-mobile" title="Created through rescheduling">
              <i class="fas fa-redo-alt"></i>
            </span>
          </h3>
        </div>
        <span class="status-badge" [ngClass]="getStatusClass(appointment.status)">
          {{ appointment.status || 'N/A' }}
        </span>
      </div>
      
      <div class="card-content">
        <div class="info-row">
          <span class="label">Date:</span>
          <span class="value">{{ formatDate(appointment.date) }}</span>
        </div>
        
        <div class="info-row">
          <span class="label">Agent Name:</span>
          <span class="value">{{ appointment.agentName || 'N/A' }}</span>
        </div>
        
        <div class="info-row">
          <span class="label">Zone:</span>
          <span class="value">{{ appointment.geoZoneName || 'N/A' }}</span>
        </div>
        
        <div class="info-row" *ngIf="appointment.timeSlot">
          <span class="label">Time Slot:</span>
          <span class="value">{{ appointment.timeSlot }}</span>
        </div>
        
        <div class="info-row" *ngIf="(appointment.remarkHistory && appointment.remarkHistory.length > 0) || appointment.remarks">
          <span class="label">Remarks:</span>
          <span class="value">{{ (appointment.remarkHistory && appointment.remarkHistory.length > 0) ? appointment.remarkHistory[appointment.remarkHistory.length - 1].text : appointment.remarks }}</span>
        </div>
      </div>
      
      <div class="card-actions">
        <button 
          class="view-appointment-btn-mobile"
          (click)="navigateToDetail(appointment)"
          title="View Appointment">
          <i class="fas fa-eye"></i> View Appointment
        </button>
        <button 
          *ngIf="canUpdateFees(appointment)"
          class="update-fees-btn-mobile"
          (click)="onUpdateFees(appointment)"
          title="Update Appointment Fees">
          <i class="fas fa-dollar-sign"></i> Update Fees
        </button>
      </div>
    </div>
  </div>

  <!-- No Data State -->
  <div *ngIf="!loading && appointments.length === 0" class="no-data-state">
    <div class="no-data-content">
      <i class="fas fa-calendar-times"></i>
      <h3>No Appointments Found</h3>
      <p>No appointments match your current filters.</p>
    </div>
  </div>

  <!-- Map View -->
  <div *ngIf="viewMode === 'map'" class="map-view-container">
    <!-- Map Controls -->
    <div class="map-controls">
      <button
        (click)="toggleTraffic()"
        class="traffic-toggle-btn"
        [class.active]="showTraffic"
        title="Toggle Traffic">
        <i class="fas fa-traffic-light"></i> Traffic {{ showTraffic ? 'On' : 'Off' }}
      </button>
    </div>
    
    <!-- Map Container -->
    <div #mapContainer class="map-container"></div>
    
    <!-- Map Loading Overlay -->
    <div *ngIf="loading || !mapInitialized" class="map-loading-overlay">
      <div class="map-loading-content">
        <i class="fas fa-spinner fa-spin"></i>
        <p>{{ loading ? 'Loading appointments...' : 'Loading map...' }}</p>
      </div>
    </div>
    
    <!-- Map Error Message -->
    <div *ngIf="viewMode === 'map' && !loading && !mapInitialized && appointments.length === 0" class="map-error-message">
      <p>No appointments to display on map</p>
    </div>
  </div>

  <!-- Pagination -->
  <div *ngIf="!loading && appointments.length > 0 && viewMode === 'grid'" class="pagination-container">
    <app-pagination
      [page]="currentPage"
      [totalPages]="totalPages"
      [pageSize]="pageSize"
      (pageChange)="onPageChange($event)"
      (pageSizeChange)="onPageSizeChange($event)">
    </app-pagination>
  </div>

  <!-- View Appointment Modal -->
  <div *ngIf="showViewAppointmentModal" class="modal-overlay">
    <div class="modal-content view-appointment-modal">
      <div class="modal-header">
        <h2>Appointment Details</h2>
        <button class="modal-close-btn" (click)="closeViewAppointmentModal()">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div *ngIf="selectedAppointmentForView" class="appointment-details">
          <p>
            <strong>Name:</strong> {{ selectedAppointmentForView.customerName || 'N/A' }}
            <span *ngIf="selectedAppointmentForView.parentId" class="rescheduled-badge-detail">
              <i class="fas fa-redo-alt"></i> Created through Rescheduling
            </span>
          </p>
          <p *ngIf="selectedAppointmentForView.parentId"><strong>Parent Appointment ID:</strong> {{ selectedAppointmentForView.parentId }}</p>
          <p><strong>Phone Number:</strong> {{ selectedAppointmentForView.phoneNumber || 'N/A' }}</p>
          <p><strong>Address:</strong> {{ selectedAppointmentForView.address || 'N/A' }}</p>
          <p><strong>Location:</strong> {{ selectedAppointmentForView.locationName || 'N/A' }}</p>
          <p><strong>Zone:</strong> {{ selectedAppointmentForView.geoZoneName || 'N/A' }}</p>
          <p><strong>PinCode:</strong> {{ selectedAppointmentForView.pinCode || 'N/A' }}</p>
          <p *ngIf="selectedAppointmentForView.location && selectedAppointmentForView.location.latitude && selectedAppointmentForView.location.longitude" class="map-link-row">
            <strong>Map:</strong> 
            <a 
              [href]="getGoogleMapsUrl(selectedAppointmentForView)" 
              target="_blank" 
              class="google-maps-link">
              <i class="fas fa-map-marker-alt"></i>
              View on Google Maps
            </a>
          </p>
          <p><strong>Date:</strong> {{ selectedAppointmentForView.date | date: 'dd/MM/yyyy' }}</p>
          <p><strong>Timeslot:</strong> {{ selectedAppointmentForView.timeSlot || 'Not specified' }}</p>
          <p><strong>Status:</strong> {{ selectedAppointmentForView.status || 'N/A' }}</p>
          <p><strong>Agent Name:</strong> {{ selectedAppointmentForView.agentName || 'N/A' }}</p>
          <p *ngIf="selectedAppointmentForView.cancellationReason"><strong>Cancellation Reason:</strong> {{ selectedAppointmentForView.cancellationReason }}</p>
          <p *ngIf="selectedAppointmentForView.dailyOrder != null"><strong>Daily Order:</strong> {{ selectedAppointmentForView.dailyOrder }}</p>
          <p *ngIf="selectedAppointmentForView.recurringAppointmentScheduleId"><strong>Recurring Schedule ID:</strong> {{ selectedAppointmentForView.recurringAppointmentScheduleId }}</p>
          <p><strong>Callback Required:</strong> {{ selectedAppointmentForView.isCallbackRequired ? 'Yes' : 'No' }}</p>
          <p><strong>Agreed:</strong> {{ selectedAppointmentForView.isAgreed ? 'Yes' : 'No' }}</p>
          <p *ngIf="selectedAppointmentForView.collectionCharge != null"><strong>Collection Charge:</strong> {{ selectedAppointmentForView.collectionCharge | currency:'INR':'symbol':'1.2-2' }}</p>
          <p *ngIf="selectedAppointmentForView.platformChargeInPercent != null"><strong>Platform Charge:</strong> {{ selectedAppointmentForView.platformChargeInPercent }}% <span *ngIf="selectedAppointmentForView.platformChargeSource">({{ selectedAppointmentForView.platformChargeSource }})</span></p>
          <p *ngIf="selectedAppointmentForView.collectionChargeSource"><strong>Collection Charge Source:</strong> {{ selectedAppointmentForView.collectionChargeSource }}</p>
          <p *ngIf="selectedAppointmentForView.createdAt"><strong>Created At:</strong> {{ selectedAppointmentForView.createdAt | date:'dd/MM/yyyy HH:mm' }}</p>
          <p *ngIf="selectedAppointmentForView.updatedAt"><strong>Updated At:</strong> {{ selectedAppointmentForView.updatedAt | date:'dd/MM/yyyy HH:mm' }}</p>
          <p *ngIf="selectedAppointmentForView.createdBy"><strong>Created By:</strong> {{ selectedAppointmentForView.createdBy }}</p>

          <!-- Scrap Items -->
          <div *ngIf="selectedAppointmentForView.scrapItems && selectedAppointmentForView.scrapItems.length > 0" class="items-section">
            <p><strong>Scrap Items:</strong></p>
            <ul class="category-list">
              <li *ngFor="let item of selectedAppointmentForView.scrapItems">{{ item.categoryName || 'N/A' }}</li>
            </ul>
          </div>

          <!-- Disposable Items -->
          <div *ngIf="selectedAppointmentForView.disposableItems && selectedAppointmentForView.disposableItems.length > 0" class="items-section">
            <p><strong>Disposable Items:</strong></p>
            <ul class="category-list">
              <li *ngFor="let item of selectedAppointmentForView.disposableItems">{{ item.categoryName || 'N/A' }}</li>
            </ul>
          </div>

          <!-- Scrap Preview Images -->
          <div *ngIf="selectedAppointmentForView.scrapPreviewImages && selectedAppointmentForView.scrapPreviewImages.length > 0" class="preview-images-section">
            <p><strong>Scrap Preview Images:</strong></p>
            <div class="preview-images-grid">
              <a
                *ngFor="let img of selectedAppointmentForView.scrapPreviewImages"
                [href]="img.previewDownloadUrl || '#'"
                target="_blank"
                class="preview-image-link"
                [title]="img.blobName || 'Preview Image'">
                <img
                  *ngIf="img.previewDownloadUrl"
                  [src]="img.previewDownloadUrl"
                  [alt]="img.blobName || 'Scrap Preview'"
                  class="preview-thumbnail" />
                <span *ngIf="!img.previewDownloadUrl" class="preview-no-url">
                  <i class="fas fa-image"></i> {{ img.blobName }}
                </span>
              </a>
            </div>
          </div>

          <div class="remarks-section">
            <p><strong>Remarks History:</strong></p>
            <div *ngIf="!selectedAppointmentForView.remarkHistory || selectedAppointmentForView.remarkHistory.length === 0" class="no-remarks">
              No remarks found in history.
            </div>
            <div class="remarks-timeline" *ngIf="selectedAppointmentForView.remarkHistory && selectedAppointmentForView.remarkHistory.length > 0">
              <div class="remark-entry" *ngFor="let remark of selectedAppointmentForView.remarkHistory">
                <div class="remark-header">
                  <span class="remark-author">{{ remark.createdBy }}</span>
                  <span class="remark-time">{{ remark.createdAt | date:'short' }}</span>
                </div>
                <div class="remark-text">{{ remark.text }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <div class="modal-footer-actions">
          <button 
            *ngIf="canConfirmAppointment(selectedAppointmentForView)"
            class="btn-accept"
            (click)="openRemarkPopup('confirm')"
            [disabled]="processingAction">
            <i class="fas fa-check"></i> Accept
          </button>
          <button 
            *ngIf="canRejectAppointment(selectedAppointmentForView)"
            class="btn-reject"
            (click)="openRemarkPopup('reject')"
            [disabled]="processingAction">
            <i class="fas fa-times"></i> Reject
          </button>
          <button 
            *ngIf="canRescheduleAppointment(selectedAppointmentForView)"
            class="btn-reschedule"
            (click)="openRescheduleModal()"
            [disabled]="processingAction || processingReschedule">
            <i class="fas fa-calendar-alt"></i> Reschedule
          </button>
          <button 
            *ngIf="canCancelAppointment(selectedAppointmentForView)"
            class="btn-cancel-appt"
            (click)="openRemarkPopup('cancel')"
            [disabled]="processingAction || processingReschedule">
            <i class="fas fa-ban"></i> Cancel
          </button>
          <button 
            *ngIf="canAssignAgent(selectedAppointmentForView)"
            class="btn-assign"
            (click)="openAssignAgentModal(selectedAppointmentForView!)"
            [disabled]="processingAction || processingReschedule">
            <i class="fas fa-user-plus"></i> Assign Agent
          </button>
          <button 
            *ngIf="canUpdateFees(selectedAppointmentForView)"
            class="btn-update-fees"
            (click)="onUpdateFees(selectedAppointmentForView!)"
            [disabled]="processingAction || processingReschedule">
            <i class="fas fa-dollar-sign"></i> Update Fees
          </button>
        </div>
        <div class="modal-footer-close">
          <button 
            class="btn-cancel"
            (click)="closeViewAppointmentModal()"
            [disabled]="processingAction || processingReschedule">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Reschedule Modal -->
  <div *ngIf="showRescheduleModal" class="modal-overlay reschedule-popup-overlay" (click)="closeRescheduleModal()">
    <div class="modal-content reschedule-popup-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>Reschedule Appointment</h2>
        <button class="modal-close-btn" (click)="closeRescheduleModal()" [disabled]="processingReschedule">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <p class="reschedule-info-text">Please provide the new date, time slot, and remarks for rescheduling this appointment.</p>
        
        <div class="form-group">
          <label for="rescheduleDate">New Date *</label>
          <input 
            type="date" 
            id="rescheduleDate"
            [(ngModel)]="rescheduleDate" 
            [min]="getCurrentDate()"
            class="reschedule-input"
            [disabled]="processingReschedule">
        </div>

        <div class="form-group">
          <label for="rescheduleTimeSlot">New Time Slot *</label>
          <select 
            id="rescheduleTimeSlot"
            [(ngModel)]="rescheduleTimeSlot" 
            class="reschedule-select"
            [disabled]="processingReschedule">
            <option value="">Select a time slot</option>
            <option *ngFor="let slot of timeSlotOptions" [value]="slot">{{ slot }}</option>
          </select>
        </div>

        <div class="form-group">
          <label for="rescheduleRemarks">Remarks</label>
          <textarea 
            id="rescheduleRemarks"
            [(ngModel)]="rescheduleRemarks" 
            rows="4" 
            class="reschedule-textarea"
            placeholder="Type any remarks about the rescheduling..."
            [disabled]="processingReschedule"></textarea>
        </div>

        <div *ngIf="rescheduleError" class="error-message-modal">
          <i class="fas fa-exclamation-circle"></i> {{ rescheduleError }}
        </div>
      </div>

      <div class="modal-footer">
        <button 
          class="btn-cancel"
          (click)="closeRescheduleModal()"
          [disabled]="processingReschedule">
          Cancel
        </button>
        <button 
          class="btn-submit-reschedule"
          (click)="submitReschedule()"
          [disabled]="!rescheduleDate || !rescheduleTimeSlot || processingReschedule">
          <span *ngIf="processingReschedule">
            <i class="fas fa-spinner fa-spin"></i> Rescheduling...
          </span>
          <span *ngIf="!processingReschedule">
            <i class="fas fa-calendar-check"></i> Reschedule
          </span>
        </button>
      </div>
    </div>
  </div>

  <!-- Remark Popup Modal -->
  <div *ngIf="showRemarkPopup" class="modal-overlay remark-popup-overlay" (click)="closeRemarkPopup()">
    <div class="modal-content remark-popup-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>Please provide a remark</h2>
        <button class="modal-close-btn" (click)="closeRemarkPopup()" [disabled]="processingAction">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <p class="remark-info-text">
          <span *ngIf="actionType === 'confirm'">A remark is required when accepting an appointment.</span>
          <span *ngIf="actionType === 'reject'">A remark is required when rejecting an appointment.</span>
          <span *ngIf="actionType === 'cancel'">Please provide a reason for cancelling this appointment.</span>
        </p>
        <div class="form-group">
          <label for="remarkTextarea">Remark:</label>
          <textarea 
            id="remarkTextarea"
            [(ngModel)]="remarkText" 
            rows="4" 
            class="remark-textarea"
            placeholder="Type your remark..."
            [disabled]="processingAction"></textarea>
        </div>

        <div *ngIf="actionError" class="error-message-modal">
          <i class="fas fa-exclamation-circle"></i> {{ actionError }}
        </div>
      </div>

      <div class="modal-footer">
        <button 
          class="btn-cancel"
          (click)="closeRemarkPopup()"
          [disabled]="processingAction">
          Cancel
        </button>
        <button 
          class="btn-submit-remark"
          [class.btn-submit-disabled]="!remarkText.trim() || processingAction"
          (click)="submitRemark()"
          [disabled]="!remarkText.trim() || processingAction">
          <span *ngIf="processingAction">
            <i class="fas fa-spinner fa-spin"></i> Processing...
          </span>
          <span *ngIf="!processingAction">
            Submit
          </span>
        </button>
      </div>
    </div>
  </div>

  <!-- Assign Agent Modal -->
  <div *ngIf="showAssignAgentModal" class="modal-overlay" (click)="closeAssignAgentModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>Assign Agent</h2>
        <button class="modal-close-btn" (click)="closeAssignAgentModal()">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div *ngIf="selectedAppointmentForAssignment" class="appointment-info">
          <p><strong>Customer:</strong> {{ selectedAppointmentForAssignment.customerName || 'N/A' }}</p>
          <p><strong>Date:</strong> {{ formatDate(selectedAppointmentForAssignment.date) }}</p>
          <p><strong>Address:</strong> {{ selectedAppointmentForAssignment.address || 'N/A' }}</p>
        </div>

        <div class="form-group">
          <label for="agentSelect">Select Agent:</label>
          <select 
            id="agentSelect"
            [(ngModel)]="selectedAgentId"
            class="agent-select"
            [disabled]="loadingAgents || assigningAgent">
            <option value="">-- Select an agent --</option>
            <option *ngFor="let agent of agents" [value]="agent.agentId">
              {{ agent.name }} ({{ agent.phoneNumber }})
            </option>
          </select>
        </div>

        <div *ngIf="loadingAgents" class="loading-message">
          <i class="fas fa-spinner fa-spin"></i> Loading agents...
        </div>

        <div *ngIf="assignAgentError" class="error-message-modal">
          <i class="fas fa-exclamation-circle"></i> {{ assignAgentError }}
        </div>
      </div>

      <div class="modal-footer">
        <button 
          class="btn-cancel"
          (click)="closeAssignAgentModal()"
          [disabled]="assigningAgent">
          Cancel
        </button>
        <button 
          class="btn-assign"
          (click)="assignAgent()"
          [disabled]="!selectedAgentId || loadingAgents || assigningAgent">
          <span *ngIf="assigningAgent">
            <i class="fas fa-spinner fa-spin"></i> Assigning...
          </span>
          <span *ngIf="!assigningAgent">
            <i class="fas fa-check"></i> Assign
          </span>
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Update Appointment Fees Modal -->
<app-update-appointment-fees
  [appointment]="selectedAppointmentForFees"
  [showModal]="showUpdateFeesModal"
  (feesUpdated)="onFeesUpdated()"
  (closeModal)="onCloseFeesModal()"
></app-update-appointment-fees>

